import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Clock, Plus, Flag, Edit2,
  Search, X, UserPlus, Loader2, Users, ChevronDown
} from 'lucide-react';
import { format, differenceInDays, isPast, isToday } from 'date-fns';
import MilestoneModal from './MilestoneModal';
import { projectBoardService } from '../api/boardApi';
import { projectService } from '../api/projectsApi';
import { userService } from '../../users';
import { toast } from 'react-hot-toast';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = ['#37559e','#bf6f50','#15803d','#b45309','#1d4ed8','#7c3aed','#0891b2','#be123c'];
const getInitials  = (name) => name ? name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
const getAvatarColor = (name) => AVATAR_PALETTE[(name?.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

const TEAM_ROLES = ['developer','designer','tester','leader','manager','analyst','marketing','pr','graphics','other'];

const normalizeUsersList = (payload) => {
  const list = Array.isArray(payload) ? payload
    : Array.isArray(payload?.users) ? payload.users
    : Array.isArray(payload?.data)  ? payload.data
    : [];
  return list.map(u => ({
    id:     u.id    || u._id    || u.user_id || '',
    name:   u.name  || u.fullName || u.full_name || u.username || u.email || 'Unknown',
    email:  u.email || '',
    role:   u.role  || '',
    skills: Array.isArray(u.skills) ? u.skills : [],
  }));
};

const StatusBadge = ({ status }) => {
  const style = (() => {
    switch (status?.toLowerCase()) {
      case 'completed':   return { backgroundColor: 'rgba(34,197,94,0.12)', color: '#166534' };
      case 'in progress': return { backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary };
      case 'on hold':     return { backgroundColor: 'rgba(245,158,11,0.14)', color: '#92400e' };
      default:            return { backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted };
    }
  })();
  return <span className="rounded-full px-3 py-1 text-sm font-medium" style={style}>{status || 'Not Set'}</span>;
};

// ─── TeamPickerModal ──────────────────────────────────────────────────────────
const TeamPickerModal = ({ projectId, currentTeam, isOpen, onClose, onSaved }) => {
  const [allUsers,  setAllUsers]  = useState([]);
  const [team,      setTeam]      = useState([]);
  const [query,     setQuery]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [roleOpen,  setRoleOpen]  = useState(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setTeam(currentTeam.map(m => ({ ...m })));
    setQuery('');
    setRoleOpen(null);
    setLoading(true);
    userService.getAllUsers()
      .then(raw => setAllUsers(normalizeUsersList(raw)))
      .catch(() => userService.getActiveUsers().then(raw => setAllUsers(normalizeUsersList(raw))))
      .finally(() => setLoading(false));
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // close role popover on outside click
  useEffect(() => {
    if (!roleOpen) return;
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setRoleOpen(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [roleOpen]);

  const memberIds = new Set(team.map(m => m.id));
  const filtered  = allUsers
    .filter(u => !memberIds.has(u.id))
    .filter(u => !query.trim() ||
      u.name.toLowerCase().includes(query.toLowerCase()) ||
      u.email.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 10);

  const addMember    = (u)    => setTeam(p => [...p, { ...u, role: u.role || 'developer' }]);
  const removeMember = (id)   => setTeam(p => p.filter(m => m.id !== id));
  const updateRole   = (id, r) => { setTeam(p => p.map(m => m.id === id ? { ...m, role: r } : m)); setRoleOpen(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = team.map(m => ({ id: m.id, name: m.name, role: m.role || '', email: m.email || '', skills: m.skills || [] }));
      await projectService.updateProject(projectId, { team: payload });
      toast.success('Team updated');
      onSaved(team);
      onClose();
    } catch {
      toast.error('Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl"
        style={{ maxHeight: '88dvh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-900">Manage Team</h3>
            <p className="text-xs text-gray-500 mt-0.5">{team.length} member{team.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X size={17} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-xl focus-within:border-[#37559e] focus-within:ring-2 focus-within:ring-[#37559e]/20 bg-white transition">
            <Search size={14} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
            />
            {loading && <Loader2 size={13} className="animate-spin text-gray-400" />}
          </div>

          {/* Search results */}
          {(query.trim() || filtered.length > 0) && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              {filtered.length === 0
                ? <p className="text-sm text-gray-400 text-center py-4">No users found</p>
                : filtered.map(u => (
                  <button key={u.id} type="button" onClick={() => addMember(u)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left border-b last:border-b-0 border-gray-100">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                      style={{ backgroundColor: getAvatarColor(u.name) }}>
                      {getInitials(u.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                    <UserPlus size={14} className="text-[#37559e] flex-shrink-0" />
                  </button>
                ))
              }
            </div>
          )}

          {/* Current team */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Current Team</p>
            {team.length === 0
              ? (
                <div className="flex flex-col items-center gap-1 py-5 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <Users size={22} className="text-gray-300" />
                  <p className="text-xs text-gray-400">No members — search above to add</p>
                </div>
              )
              : (
                <div ref={dropRef} className="space-y-2">
                  {team.map(m => (
                    <div key={m.id} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                        style={{ backgroundColor: getAvatarColor(m.name) }}>
                        {getInitials(m.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{m.name}</p>
                        <p className="text-xs text-gray-500 truncate">{m.email}</p>
                      </div>
                      {/* Role picker */}
                      <div className="relative flex-shrink-0">
                        <button type="button" onClick={() => setRoleOpen(roleOpen === m.id ? null : m.id)}
                          className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-[#37559e] hover:text-[#37559e] transition-colors">
                          {m.role || 'role'}
                          <ChevronDown size={10} />
                        </button>
                        {roleOpen === m.id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1 min-w-[120px]">
                            {TEAM_ROLES.map(r => (
                              <button key={r} type="button" onClick={() => updateRole(m.id, r)}
                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 transition-colors ${m.role === r ? 'text-[#37559e] font-semibold' : 'text-gray-700'}`}>
                                {r}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button type="button" onClick={() => removeMember(m.id)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/60">
          <button type="button" onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            {saving && <Loader2 size={13} className="animate-spin" />}
            {saving ? 'Saving…' : 'Save Team'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ProjectOverview ──────────────────────────────────────────────────────────
const ProjectOverview = ({ project, tasks, team: teamProp, milestones = [], onRefresh, canEditTeam = false }) => {
  const [localTeam,       setLocalTeam]       = useState(teamProp || []);
  const [isTeamPickerOpen, setIsTeamPickerOpen] = useState(false);
  const [isModalOpen,     setIsModalOpen]     = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  useEffect(() => { setLocalTeam(teamProp || []); }, [teamProp]);

  // ── Task stats ─────────────────────────────────────────────────────────────
  const calcProgress = useCallback(() => {
    const nt = (tasks || []).filter(t => t.status !== 'trash');
    if (!nt.length) return { percentage: 0, completed: 0, total: 0 };
    const done = nt.filter(t => t.status === 'done').length;
    return { percentage: Math.round(done / nt.length * 100), completed: done, total: nt.length };
  }, [tasks]);

  const { percentage: pct, completed: doneTasks, total: totalTasks } = calcProgress();
  const taskDist = (tasks || []).reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  const endDate      = project?.end_date || project?.endDate;
  const daysLeft     = endDate ? differenceInDays(new Date(endDate), new Date()) : null;

  // ── Milestones ─────────────────────────────────────────────────────────────
  const parseMsDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && typeof d.toDate === 'function') return d.toDate();
    const p = new Date(d);
    return isNaN(p.getTime()) ? null : p;
  };

  const getMsStatus = (ms) => {
    if (ms.status === 'completed') return 'completed';
    const d = parseMsDate(ms?.due_date || ms?.dueDate);
    if (!d) return 'upcoming';
    if (isPast(d) && !isToday(d)) return 'overdue';
    if (isToday(d)) return 'in progress';
    return 'upcoming';
  };

  const upcomingMilestones = [...milestones]
    .sort((a, b) => { const ad = parseMsDate(a?.due_date || a?.dueDate), bd = parseMsDate(b?.due_date || b?.dueDate); if (ad && bd) return ad - bd; return ad ? -1 : 1; })
    .slice(0, 5);

  const handleMilestoneSave = async (formData) => {
    try {
      if (selectedMilestone) {
        await projectBoardService.updateMilestone(project.id, selectedMilestone.id, formData);
        toast.success('Milestone updated');
      } else {
        await projectBoardService.createMilestone(project.id, formData);
        toast.success('Milestone created');
      }
      setIsModalOpen(false);
      setSelectedMilestone(null);
      if (onRefresh) await onRefresh();
    } catch {
      toast.error('Failed to save milestone');
    }
  };

  const handleTeamSaved = (updatedTeam) => {
    setLocalTeam(updatedTeam);
    if (onRefresh) onRefresh();
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* ── Left (3 cols) ─────────────────────────────────────────────── */}
        <div className="space-y-4 lg:col-span-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Status card */}
            <div className="rounded-2xl border p-4" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Project Status</h3>
                  <StatusBadge status={project?.status} />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="relative h-24 w-24 flex-shrink-0">
                  <svg className="h-full w-full transform -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#E5E7EB" strokeWidth="2.5" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke={BRAND_COLORS.primary} strokeWidth="2.5"
                      strokeDasharray={`${pct}, 100`} strokeLinecap="round" transform="rotate(-90 18 18)" />
                    <text x="18" y="18" dominantBaseline="middle" textAnchor="middle"
                      className="font-medium fill-current text-gray-700" style={{ fontSize: '6px' }} transform="rotate(90 18 18)">
                      {pct}%
                    </text>
                  </svg>
                </div>
                <div className="space-y-0.5">
                  <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Overall Progress</p>
                  <p className="font-medium text-sm" style={{ color: NEUTRAL_COLORS.black }}>{doneTasks} of {totalTasks} tasks</p>
                  <div className="flex flex-wrap gap-1 text-xs">
                    {Object.entries(taskDist).map(([s, c]) => (
                      <span key={s} className={`px-1.5 py-0.5 rounded-full ${
                        s === 'done' ? 'bg-green-100 text-green-800' :
                        s === 'review' ? 'bg-amber-100 text-amber-800' :
                        s === 'inProgress' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'}`}>
                        {c} {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Time remaining */}
            <div className="rounded-2xl border p-4" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
              <h3 className="text-base font-semibold mb-3" style={{ color: NEUTRAL_COLORS.black }}>Time Remaining</h3>

              {/* Days count */}
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
                  <Clock className="h-4 w-4" style={{ color: BRAND_COLORS.primary }} />
                </div>
                <div>
                  <p className={`text-2xl font-bold leading-tight ${
                    daysLeft === null        ? 'text-gray-400' :
                    daysLeft < 0            ? 'text-red-600'  :
                    daysLeft <= 7           ? 'text-amber-600': 'text-gray-900'}`}>
                    {daysLeft !== null ? Math.abs(daysLeft) : '—'}
                    <span className="text-sm font-medium ml-1" style={{ color: SYSTEM_COLORS.textMuted }}>
                      {daysLeft !== null ? (daysLeft < 0 ? 'days overdue' : 'days left') : 'no end date'}
                    </span>
                  </p>
                </div>
              </div>

              {/* Start / End dates */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg px-3 py-2" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>Start</p>
                  <p className="text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                    {project?.start_date || project?.startDate
                      ? format(new Date(project.start_date || project.startDate), 'MMM d, yyyy')
                      : <span className="text-gray-400 font-normal">Not set</span>}
                  </p>
                </div>
                <div className="rounded-lg px-3 py-2" style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>End</p>
                  <p className={`text-sm font-medium ${daysLeft !== null && daysLeft < 0 ? 'text-red-600' : ''}`}
                    style={daysLeft === null || daysLeft >= 0 ? { color: NEUTRAL_COLORS.black } : {}}>
                    {endDate
                      ? format(new Date(endDate), 'MMM d, yyyy')
                      : <span className="text-gray-400 font-normal">Not set</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones */}
          <div className="rounded-2xl border p-4" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Milestones</h3>
              <button
                onClick={() => { setSelectedMilestone(null); setIsModalOpen(true); }}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
                style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
              >
                <Plus size={13} /> Add Milestone
              </button>
            </div>

            {upcomingMilestones.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {upcomingMilestones.map(ms => {
                  const st       = getMsStatus(ms);
                  const progress = Math.max(0, Math.min(100, Number(ms.progress) || 0));
                  const dueDate  = parseMsDate(ms.due_date || ms.dueDate);
                  const dLeft    = dueDate ? differenceInDays(dueDate, new Date()) : null;

                  const MS_STATUS = {
                    completed:    { label: 'Completed',   bg: '#f0fdf4', text: '#15803d', dot: '#22c55e' },
                    overdue:      { label: 'Overdue',     bg: '#fff1f2', text: '#be123c', dot: '#f43f5e' },
                    'in progress':{ label: 'In Progress', bg: '#eff6ff', text: '#1d4ed8', dot: '#3b82f6' },
                    upcoming:     { label: 'Upcoming',    bg: '#f8fafc', text: '#475569', dot: '#94a3b8' },
                  };
                  const sc = MS_STATUS[st] || MS_STATUS.upcoming;
                  const progressColor = progress >= 75 ? '#22c55e' : progress >= 40 ? BRAND_COLORS.primary : '#f59e0b';
                  const dueDateStyle  = dLeft === null     ? { color: SYSTEM_COLORS.textMuted }
                                      : dLeft < 0         ? { color: '#dc2626', fontWeight: 600 }
                                      : dLeft <= 7        ? { color: '#ea580c', fontWeight: 600 }
                                      : { color: SYSTEM_COLORS.textMuted };

                  return (
                    <div
                      key={ms.id}
                      className="flex flex-col rounded-2xl border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                      style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                    >
                      {/* Card body */}
                      <div className="flex flex-col gap-2.5 p-4 flex-1">

                        {/* Title row + edit */}
                        <div className="flex items-start gap-2">
                          <h4 className="flex-1 text-sm font-bold leading-snug" style={{ color: NEUTRAL_COLORS.black }}>
                            {ms.title}
                          </h4>
                          <button
                            onClick={() => { setSelectedMilestone(ms); setIsModalOpen(true); }}
                            className="flex-shrink-0 flex items-center justify-center rounded-lg transition-colors"
                            style={{ width: 26, height: 26, color: SYSTEM_COLORS.textMuted }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = SYSTEM_COLORS.borderSoft; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <Edit2 size={13} />
                          </button>
                        </div>

                        {/* Description */}
                        {ms.description && (
                          <p className="text-xs line-clamp-2 leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>
                            {ms.description}
                          </p>
                        )}

                        {/* Status badge */}
                        <div>
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                            style={{ backgroundColor: sc.bg, color: sc.text }}
                          >
                            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: sc.dot }} />
                            {sc.label}
                          </span>
                        </div>

                        {/* Progress */}
                        <div
                          className="space-y-1.5 rounded-lg px-3 py-2.5"
                          style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle || '#f8fafc' }}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>Progress</span>
                            <span className="text-xs font-bold tabular-nums" style={{ color: NEUTRAL_COLORS.black }}>{progress}%</span>
                          </div>
                          <div className="w-full h-1.5 overflow-hidden rounded-full" style={{ backgroundColor: SYSTEM_COLORS.borderSoft }}>
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${progress}%`, backgroundColor: progressColor }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Footer — due date */}
                      <div
                        className="flex items-center px-4 py-2.5 rounded-b-2xl"
                        style={{ borderTop: `1px solid ${SYSTEM_COLORS.borderSoft}` }}
                      >
                        <div className="flex items-center gap-1.5 text-xs" style={dueDateStyle}>
                          <Flag size={11} />
                          <span>{dueDate ? format(dueDate, 'MMM d, yyyy') : 'No due date'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                className="flex flex-col items-center gap-2 py-8 rounded-xl border border-dashed"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}
              >
                <Flag size={24} style={{ color: SYSTEM_COLORS.textSoft }} />
                <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No milestones yet</p>
                <button
                  onClick={() => { setSelectedMilestone(null); setIsModalOpen(true); }}
                  className="text-xs font-semibold mt-0.5"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  Add first milestone
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Team card (right col) ─────────────────────────────────────── */}
        <div className="rounded-2xl border p-4" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowSoft }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Team</h3>
            {canEditTeam && (
              <button
                onClick={() => setIsTeamPickerOpen(true)}
                title="Manage team"
                className="flex items-center justify-center w-6 h-6 rounded-full text-white transition-colors"
                style={{ backgroundColor: BRAND_COLORS.primary }}
              >
                <Plus size={14} />
              </button>
            )}
          </div>

          {localTeam.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <Users size={24} className="text-gray-300" />
              <p className="text-sm text-gray-400">No team members</p>
              {canEditTeam && (
                <button
                  onClick={() => setIsTeamPickerOpen(true)}
                  className="text-xs font-medium mt-1"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  Add members
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {localTeam.map(m => (
                <div key={m.id} className="flex items-start gap-2.5 group">
                  {/* Avatar */}
                  <div
                    className="h-9 w-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 overflow-hidden"
                    style={{ backgroundColor: getAvatarColor(m.name) }}
                  >
                    {(m.photoUrl || m.photo_url || m.profileImage)
                      ? <img src={m.photoUrl || m.photo_url || m.profileImage} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : getInitials(m.name)
                    }
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{m.name}</p>
                    {m.role && <p className="text-xs text-gray-400 truncate leading-tight capitalize">{m.role}</p>}
                    {/* Skills */}
                    {Array.isArray(m.skills) && m.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {m.skills.map((skill, si) => (
                          <span
                            key={si}
                            className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium leading-tight"
                            style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {canEditTeam && (
                <button
                  onClick={() => setIsTeamPickerOpen(true)}
                  className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed text-xs font-medium transition-colors hover:bg-blue-50"
                  style={{ borderColor: BRAND_COLORS.primary, color: BRAND_COLORS.primary }}
                >
                  <Plus size={12} /> Edit team
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Milestone modal */}
      <MilestoneModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedMilestone(null); }}
        onSave={handleMilestoneSave}
        projectId={project?.id}
        milestone={selectedMilestone}
        teamMembers={localTeam}
      />

      {/* Team picker modal — only mounted when the user has edit rights */}
      {canEditTeam && (
        <TeamPickerModal
          projectId={project?.id}
          currentTeam={localTeam}
          isOpen={isTeamPickerOpen}
          onClose={() => setIsTeamPickerOpen(false)}
          onSaved={handleTeamSaved}
        />
      )}
    </>
  );
};

export default ProjectOverview;
